import { cn } from "@/utils/utils";
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
  type TouchEvent as ReactTouchEvent,
  type WheelEvent as ReactWheelEvent
} from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { AnimatePresence } from "framer-motion";
import { ReservedUserIds, UserData } from "@/entities/users/types";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { messagesCompareFn } from "@/components/layouts/Chat/utils";
import {
  DrawerFooter,
  DrawerHeader
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { useTranslation } from "react-i18next";
import {
  BellRing,
  Close,
  DreamhubGradient,
  MessageCircle,
  Performance,
  Search,
  Star
} from "@dreamhub/designsystem/icons";
import LayoutTabs from "@/components/controls/LayoutTabs";
import CounterBadge from "@/components/ui/counter-badge";
import {
  useMarkReportsSeen,
  useReportsUnseenCount
} from "@/entities/savedReports/hooks";
import { getCurrentTimeUTC, simpleDate } from "@/utils/datetime";
import { capitalize } from "lodash";
import {
  ChatMessage,
  DATE_SEPARATOR_PREFIX,
  FixedMessageIds,
  NO_ANSWER_MSG_PREFIX,
  ReservedMessageIds,
  dreamUser
} from "@/entities/messaging/types";
import { ChatBalloon } from "./ChatBalloon";
import { computeFreshMessageIds } from "./chatEntryAnimation";
import Text from "@/components/ui/text";
import FeedbackButton from "@/components/controls/FeedbackButton";
import BetaBadge from "@/components/ui/beta-badge";
import { isToday, isYesterday, isSameDay } from "date-fns";
import {
  findDraftMessageIndex,
  isDateSeparatorMessage,
  isReservedMessage
} from "@/entities/messaging/helpers";
import { IconButton } from "@/components/ui/icon-button";
import { ReportsPanel } from "@/components/global/Chat/components/dreamerReports/ReportsPanel";
import { ReportPreviewHost } from "@/components/global/Chat/components/dreamerReports/ReportPreviewHost";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import {
  LEGACY_TABS_IDS,
  REPORTS_TABS_IDS,
  filterMessagesBySlicer,
  isChatTurnMessage
} from "./chatSlicer";
import { ChatTabId, ChatTabsProvider } from "./ChatTabsContext";
import NewChatBottomBar from "@/components/global/Chat/components/chat/NewChatBottomBar";
import { DreamerSearchBar } from "@/components/global/Chat/components/chat/DreamerSearchBar";
import { useDreamerSearch } from "@/components/global/Chat/components/chat/useDreamerSearch";
import { useAnalytics } from "@/services/analytics/hook";
import IconContainer from "@/components/ui/icon-container";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useAuth } from "@/services/auth/AuthContext";
import { useAttachmentDownloader } from "@/entities/attachments/hooks";

interface ChatListProps {
  selectedUser: UserData;
  sendMessageCallback: (newMessage: ChatMessage) => void;
  onStopResponse: () => void;
  onRetry: (originalMessage: string) => void;
  onRetryFailedAnswer: (question: ChatMessage, errorMessageId: string) => void;
  onRegenerateDraft: (messageId: string, guidance?: string) => void;
  onSelectDraftVersion: (
    messageId: string,
    version: number,
    onFailure: () => void
  ) => void;
  regeneratingDraftMessageIds: Set<string>;
  setChatDrawerOpen: Dispatch<SetStateAction<boolean>>;
  chatDrawerOpen: boolean;
  // Draft card to bring into view (deep link); cleared via onFocusDraftHandled once scrolled to or given up on.
  focusDraftId: string | null;
  onFocusDraftHandled: () => void;
  // Incremented each time a question is sent from the home header.
  headerSendCount?: number;
}

// Keyed by tab id. The legacy ids map to the same icons so the strip keeps them
// while the flag is still resolving.
const TAB_ICONS: { [key: string]: JSX.Element } = {
  chat: <MessageCircle />,
  chats: <MessageCircle />,
  alerts: <BellRing />,
  reports: <Performance />,
  starred: <Star />
};

// Must exceed conversational's brain-flow turn budget (300s, CRM-9810) with margin.
export const RESPONSE_TIMEOUT_MS = 315000; // 5 minutes and 15 seconds
// Must exceed RESPONSE_TIMEOUT_MS — any message younger than the response timeout may still be in-flight.
export const STALE_MESSAGE_THRESHOLD_MS = 6 * 60 * 1000;

// Trailing spacer so the last message clears the bottom gradient (legacy parity); stable module refs so Virtuoso doesn't re-init.
const VirtuosoListFooter = () => <div className="h-8 w-full shrink-0" />;
const VIRTUOSO_COMPONENTS = { Footer: VirtuosoListFooter };
// Pre-render this far beyond the viewport so height corrections land off-screen.
const VIRTUOSO_VIEWPORT_MARGIN = { top: 600, bottom: 600 };
// Px from the bottom that still counts as "at bottom" for follow.
const VIRTUOSO_AT_BOTTOM_THRESHOLD = 100;
// Re-assert the deep-linked draft's position past the portal mount (~180ms) and late chart/table re-measures.
const DRAFT_FOCUS_DELAYS_MS = [200, 450, 900];
// Stop waiting for the deep-linked draft to arrive; the drawer stays open at the latest message.
const DRAFT_FOCUS_TIMEOUT_MS = 10000;

export function ChatList({
  selectedUser,
  sendMessageCallback,
  onStopResponse,
  onRetry,
  onRetryFailedAnswer,
  onRegenerateDraft,
  onSelectDraftVersion,
  regeneratingDraftMessageIds,
  setChatDrawerOpen,
  chatDrawerOpen,
  focusDraftId,
  onFocusDraftHandled,
  headerSendCount = 0
}: ChatListProps) {
  const { user } = useAuth();
  // The in-app viewer is the recipient, so the meeting-prep greeting is personalized to them.
  // Computed once here (not inside ChatBalloon) so an AuthContext change — e.g. a token refresh —
  // doesn't re-render every mounted balloon via a direct context subscription, which would bypass
  // ChatBalloon's memo. Guards against a Frontegg display name that's really an email fallback.
  const recipientFirstName = useMemo(() => {
    const firstName = user?.name?.trim().split(/\s+/)[0];
    return firstName && !firstName.includes("@") ? firstName : undefined;
  }, [user?.name]);
  // Created here for the same reason as recipientFirstName: it reads the auth context.
  const downloadAttachment = useAttachmentDownloader();

  // Legacy (non-virtualized) scroll viewport; the DOM search hook indexes it too.
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  // followOutput only re-scrolls on a new item, not when the last item grows (a table/chart loading rows). Re-pin on any height change while following; disengage on scroll-up.
  const followBottomRef = useRef(true);
  const virtuosoScrollerRef = useRef<HTMLElement | null>(null);
  // Topmost rendered message id, so opening search (which de-virtualizes) can return to the user's place on close.
  const topVisibleIdRef = useRef<string | null>(null);
  // Top-visible message id + px scrolled above the fold, captured on close and consumed on reopen. Anchored by id (not index) so a message inserted above the anchor while closed can't shift what we restore to. The ref is the effect/timer data channel; the state mirror below is the render-safe copy (reading the ref during render would break purity).
  const virtuosoRestoreRef = useRef<{ id: string; offset: number } | null>(
    null
  );
  const [virtuosoRestoreAnchor, setVirtuosoRestoreAnchor] = useState<{
    id: string;
    offset: number;
  } | null>(null);
  const prevDrawerOpenForSnapshotRef = useRef(chatDrawerOpen);
  const virtuosoRestoreCleanupRef = useRef<(() => void) | null>(null);

  // Legacy-path scroll state — lives here (ChatList never unmounts) so it survives the portal remount, mirroring main.
  const isScrollingToBottomRef = useRef(false);
  // User intentionally scrolled up (set on wheel/touch only — not on scroll, to dodge overflow-anchor false positives); cleared near the bottom.
  const userScrolledUpRef = useRef(false);
  // Scroll offset when the drawer closed; re-applied on reopen (null = never saved).
  const savedScrollTopRef = useRef<number | null>(null);
  const legacyCleanupRef = useRef<(() => void) | null>(null);
  const prevLastMessageIdRef = useRef<string>("");
  const prevDrawerOpenRef = useRef<boolean>(false);
  const prevSliceRef = useRef<string>("");
  const prevResponseRequestIdRef = useRef<string | null>(null);
  // Last finger Y, so each touchmove is read as a scroll toward older (up) or newer (down) messages.
  const lastTouchYRef = useRef<number | null>(null);
  const recordTouchStart = useCallback((event: TouchEvent) => {
    lastTouchYRef.current =
      event.touches.length === 1 ? event.touches[0].clientY : null;
  }, []);
  // A finger moving down scrolls the view up; multi-touch (pinch) is no scroll. Advances lastTouchYRef, so call it once per touchmove.
  const touchScrollDirection = useCallback(
    (event: TouchEvent): "up" | "down" | null => {
      if (event.touches.length !== 1) {
        lastTouchYRef.current = null;
        return null;
      }
      const lastY = lastTouchYRef.current;
      const currentY = event.touches[0].clientY;
      lastTouchYRef.current = currentY;
      // First single-finger move after a pinch: lifting a finger fires no touchstart, so seed here.
      if (lastY === null) return null;
      if (currentY === lastY) return null;
      return currentY > lastY ? "up" : "down";
    },
    []
  );
  // Only user input moving toward the bottom re-engages follow: atBottomStateChange fires on a transition alone (it covers scrolling back across the threshold), and a scroll listener would also catch programmatic scrolls (draft focus, reopen restore) that disengage on purpose.
  const resumeFollowingIfNearBottom = useCallback(() => {
    const scroller = virtuosoScrollerRef.current;
    if (!scroller) return;
    const distanceFromBottom =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (distanceFromBottom <= VIRTUOSO_AT_BOTTOM_THRESHOLD) {
      followBottomRef.current = true;
    }
  }, []);
  const updateFollowOnWheel = useCallback(
    (event: WheelEvent) => {
      if (event.deltaY < 0) followBottomRef.current = false;
      else if (event.deltaY > 0) resumeFollowingIfNearBottom();
    },
    [resumeFollowingIfNearBottom]
  );
  const updateFollowOnTouch = useCallback(
    (event: TouchEvent) => {
      const direction = touchScrollDirection(event);
      if (direction === "up") followBottomRef.current = false;
      else if (direction === "down") resumeFollowingIfNearBottom();
    },
    [touchScrollDirection, resumeFollowingIfNearBottom]
  );
  // Keyboard scroll-up (PageUp/Home/ArrowUp) is a deliberate move off the bottom — disengage follow so a streamed token doesn't yank the view down.
  const updateFollowOnKey = useCallback(
    (event: KeyboardEvent) => {
      // Keys typed in a field inside a message (e.g. a draft's regenerate prompt) move a caret, not the list.
      const target = event.target;
      if (
        event.defaultPrevented ||
        (target instanceof HTMLElement &&
          (target.isContentEditable ||
            target.closest("input, textarea, select")))
      ) {
        return;
      }
      if (
        event.key === "PageUp" ||
        event.key === "Home" ||
        event.key === "ArrowUp"
      ) {
        followBottomRef.current = false;
      } else if (
        event.key === "PageDown" ||
        event.key === "End" ||
        event.key === "ArrowDown"
      ) {
        resumeFollowingIfNearBottom();
      }
    },
    [resumeFollowingIfNearBottom]
  );
  const setVirtuosoScroller = useCallback(
    (ref: HTMLElement | Window | null) => {
      const previous = virtuosoScrollerRef.current;
      if (previous) {
        previous.removeEventListener("wheel", updateFollowOnWheel);
        previous.removeEventListener("touchstart", recordTouchStart);
        previous.removeEventListener("touchmove", updateFollowOnTouch);
        previous.removeEventListener("keydown", updateFollowOnKey);
      }
      const element = ref instanceof HTMLElement ? ref : null;
      virtuosoScrollerRef.current = element;
      if (element) {
        // Markers the non-virtualized list carries, so ChartMessage's expand-into-view (closest('[data-id=chat-container]')) and e2e selectors keep working.
        element.setAttribute("data-id", "chat-container");
        element.setAttribute("data-test", "chat-container");
        element.addEventListener("wheel", updateFollowOnWheel, {
          passive: true
        });
        element.addEventListener("touchstart", recordTouchStart, {
          passive: true
        });
        element.addEventListener("touchmove", updateFollowOnTouch, {
          passive: true
        });
        element.addEventListener("keydown", updateFollowOnKey, {
          passive: true
        });
      }
    },
    [
      updateFollowOnWheel,
      recordTouchStart,
      updateFollowOnTouch,
      updateFollowOnKey
    ]
  );
  // Scroll to the true bottom (past the footer spacer) via the scroller.
  const pinVirtualizedToBottom = useCallback((smooth = false) => {
    const scroller = virtuosoScrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({
      top: scroller.scrollHeight,
      behavior: smooth ? "smooth" : "auto"
    });
  }, []);
  const responseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutFallbackCounterRef = useRef(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Message id to reopen Virtuoso at after search de-virtualized the list (null = latest); drawer close/reopen uses virtuosoRestoreRef, not this.
  const [reopenAnchorId, setReopenAnchorId] = useState<string | null>(null);
  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const {
    messages,
    pendingUserMessage,
    waitingForMessage,
    setWaitingForMessage,
    thoughtMessage,
    addMessages,
    lastResponseRequestId,
    stoppedRequestIds,
    pendingRequestId,
    retryingQuestionId
  } = useChat();
  const [displayMessages, setDisplayMessages] = useState<ChatMessage[]>([]);
  const { t } = useTranslation();
  const [sliceSelected, setSlicerSelected] = useState<string>(
    LEGACY_TABS_IDS[0]
  );
  const [lastMessageInFocusTimestamp, setLastMessageInFocusTimestamp] =
    useState(0);
  // Virtualized-path scroll-to-bottom affordance.
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // Legacy-path scroll-to-bottom affordance (driven by handleScroll).
  const [showLegacyScrollToBottom, setShowLegacyScrollToBottom] =
    useState(false);

  const analytics = useAnalytics();
  const { enabled: dreamerSearchEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8132_DREAMER_SEARCH
  );
  // Own flag so this high-risk shared-surface change rolls out/back independently; OFF = legacy list.
  const { enabled: virtualizationEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8658_CHAT_VIRTUALIZATION
  );
  // Gates the whole tab set, not just the Reports tab: dropping "All" is the
  // most visible part of this change and has to roll back on its own.
  const { enabled: reportsTabEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10285_MY_REPORTS
  );
  const tabIds = reportsTabEnabled ? REPORTS_TABS_IDS : LEGACY_TABS_IDS;
  // A report opened from outside the reports list, e.g. run again from an
  // answer card, shows here whichever tab is selected.
  const { focusedReport, clearFocusedReport } = useReportRuns();
  const defaultSlicer = tabIds[0];
  const reportsTabSelected = sliceSelected === "reports";
  const { unseenCount: unseenReportsCount, refetch: refetchUnseenReports } =
    useReportsUnseenCount(reportsTabEnabled);
  const { mutate: markReportsSeen } = useMarkReportsSeen();

  // Reports saved from another device or tab since the drawer was last open.
  useEffect(() => {
    if (reportsTabEnabled && chatDrawerOpen) refetchUnseenReports();
  }, [reportsTabEnabled, chatDrawerOpen, refetchUnseenReports]);

  // Looking at the Reports tab is what clears its badge, the way opening the
  // drawer clears Dreamer's. A success releases the guard, so a report saved
  // while the tab stays open is marked too; a failure keeps it set, because the
  // failed write puts the count back and retrying on that would never stop.
  // Each success re-runs the check, for a count that rose while it was sent.
  const isViewingReportsTab =
    reportsTabEnabled && chatDrawerOpen && reportsTabSelected;
  const markedSeenThisVisitRef = useRef(false);
  const [markSeenSuccessCount, setMarkSeenSuccessCount] = useState(0);
  useEffect(() => {
    if (!isViewingReportsTab) {
      markedSeenThisVisitRef.current = false;
      return;
    }
    if (unseenReportsCount > 0 && !markedSeenThisVisitRef.current) {
      markedSeenThisVisitRef.current = true;
      markReportsSeen(
        {},
        {
          onSuccess: () => {
            markedSeenThisVisitRef.current = false;
            setMarkSeenSuccessCount((count) => count + 1);
          }
        }
      );
    }
  }, [
    isViewingReportsTab,
    unseenReportsCount,
    markReportsSeen,
    markSeenSuccessCount
  ]);

  // The flag resolves asynchronously, so the first render always uses the
  // legacy set and sliceSelected would otherwise stay on an id the resolved set
  // no longer has — filterBySlicer would fall through to its default and the
  // tab would render empty while the strip looked fine.
  useEffect(() => {
    if (!tabIds.includes(sliceSelected)) {
      setSlicerSelected(defaultSlicer);
    }
  }, [tabIds, sliceSelected, defaultSlicer]);

  // The deep-linked draft is a Dreamer reply, which only the default tab is sure to list.
  useEffect(() => {
    if (focusDraftId) setSlicerSelected(defaultSlicer);
  }, [focusDraftId, defaultSlicer]);

  // A question sent from the home header is answered on the default tab, so show it there.
  // Tracked so a later tab-set change (flag resolving) doesn't re-apply an old send.
  const handledHeaderSendRef = useRef(headerSendCount);
  useEffect(() => {
    if (headerSendCount === handledHeaderSendRef.current) return;
    handledHeaderSendRef.current = headerSendCount;
    setSlicerSelected(defaultSlicer);
  }, [headerSendCount, defaultSlicer]);

  const chatTabs = useMemo(
    () => ({ showTab: (tab: ChatTabId) => setSlicerSelected(tab) }),
    []
  );

  // The Reports tab has nothing for the message search to match, and leaving it
  // open would keep the list unvirtualized behind a bar the user cannot see.
  useEffect(() => {
    if (reportsTabSelected) setIsSearchOpen(false);
  }, [reportsTabSelected]);
  // Gated on the flag so the search side effects never run when it is off.
  const search = useDreamerSearch({
    containerRef: messagesContainerRef,
    isOpen: dreamerSearchEnabled && isSearchOpen
  });

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    // Recompute auto-follow from the real position (opening search forced userScrolledUpRef true).
    const container = messagesContainerRef.current;
    if (container) {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      userScrolledUpRef.current = distanceFromBottom > 100;
    }
    // Restore focus to the toggle that opened the bar (keyboard/Escape close).
    searchToggleRef.current?.focus();
  }, []);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((open) => !open);
  }, []);

  // --- Legacy-path scroll handlers (mirror main) ---
  const handleScroll = useCallback(() => {
    if (isScrollingToBottomRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowLegacyScrollToBottom(distanceFromBottom > 100);
    // Clear the flag when the user scrolls back to near the bottom.
    if (distanceFromBottom <= 100) {
      userScrolledUpRef.current = false;
    }
  }, []);

  // Only genuine upward user input marks scrolled-up, avoiding overflow-anchor false positives. A downward wheel or drag at the bottom moves nothing, so no scroll event would ever clear it.
  const markScrolledUp = useCallback(() => {
    userScrolledUpRef.current = true;
  }, []);
  const handleUserWheel = useCallback(
    (event: ReactWheelEvent) => {
      if (event.deltaY < 0) markScrolledUp();
    },
    [markScrolledUp]
  );
  const handleUserTouchStart = useCallback(
    (event: ReactTouchEvent) => recordTouchStart(event.nativeEvent),
    [recordTouchStart]
  );
  const handleUserTouchMove = useCallback(
    (event: ReactTouchEvent) => {
      if (touchScrollDirection(event.nativeEvent) === "up") markScrolledUp();
    },
    [touchScrollDirection, markScrolledUp]
  );

  const scrollToBottomLegacy = useCallback(() => {
    isScrollingToBottomRef.current = true;
    setShowLegacyScrollToBottom(false);
    userScrolledUpRef.current = false;
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      const checkDone = () => {
        if (!container) return;
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceFromBottom < 5) {
          isScrollingToBottomRef.current = false;
        } else {
          requestAnimationFrame(checkDone);
        }
      };
      requestAnimationFrame(checkDone);
    }
  }, []);

  const handleResizeEnd = useCallback(
    (newWidth: number) => {
      analytics.track("dreamer_resize", {
        width: newWidth
      });
    },
    [analytics]
  );

  const getDateSeparatorText = useCallback(
    (date: Date): string => {
      if (isToday(date)) {
        return capitalize(t("dateTime.today"));
      }
      if (isYesterday(date)) {
        return capitalize(t("dateTime.yesterday"));
      }
      return simpleDate(date, "short");
    },
    [t]
  );

  const createDateSeparator = useCallback(
    (date: Date): ChatMessage => ({
      id: `${DATE_SEPARATOR_PREFIX}${date.toDateString()}`,
      message: getDateSeparatorText(date),
      timestamp: date.getTime().toString(),
      user: dreamUser
    }),
    [getDateSeparatorText]
  );

  const insertDateSeparators = useCallback(
    (msgs: ChatMessage[]): ChatMessage[] => {
      if (msgs.length === 0) return msgs;

      const result: ChatMessage[] = [];
      let currentDate: Date | null = null;

      for (const msg of msgs) {
        if (isReservedMessage(msg) || isDateSeparatorMessage(msg)) {
          result.push(msg);
          continue;
        }

        const msgDate = new Date(parseInt(msg.timestamp, 10) * 1000);

        // If this is the first message or it's from a different day
        if (!currentDate || !isSameDay(currentDate, msgDate)) {
          result.push(createDateSeparator(msgDate));
          currentDate = msgDate;
        }

        result.push(msg);
      }

      return result;
    },
    [createDateSeparator]
  );

  const getLastMessage = (msgs: ChatMessage[]): ChatMessage => {
    return msgs[msgs.length - 1];
  };

  const handleFocus = () => {
    const lastMessage = getLastMessage(displayMessages);
    const timestamp = lastMessage?.timestamp;
    setLastMessageInFocusTimestamp(timestamp ? parseInt(timestamp, 10) : 0);
  };

  const handleSlicer = (value: string) => {
    setSlicerSelected(value);
    handleFocus();
  };

  // Clear timeout when component unmounts or when waiting state changes
  const clearResponseTimeout = useCallback(() => {
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
  }, []);

  // Memoize the waiting state calculation to avoid recalculating on every render
  // Messages from context are already deduplicated and sorted
  const { shouldShowWaiting, staleUnansweredMessageId, waitingMessageId } =
    useMemo(() => {
      // Find the last message in the user-dreamer conversation (excluding system, mentor, alert, and notification messages)
      // Iterate from end to avoid creating a reversed copy
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (isChatTurnMessage(msg)) {
          const isStoppedRequest =
            msg.isStopped ||
            (!!msg.requestId && stoppedRequestIds.has(msg.requestId));
          if (msg.user?.id !== selectedUser.id || isStoppedRequest) {
            return {
              shouldShowWaiting: false,
              staleUnansweredMessageId: null,
              waitingMessageId: null
            };
          }
          const isStale =
            msg.id !== retryingQuestionId &&
            Date.now() - parseInt(msg.timestamp, 10) * 1000 >
              STALE_MESSAGE_THRESHOLD_MS;
          if (isStale) {
            return {
              shouldShowWaiting: false,
              staleUnansweredMessageId: msg.id,
              waitingMessageId: null
            };
          }
          return {
            shouldShowWaiting: true,
            staleUnansweredMessageId: null,
            waitingMessageId: msg.requestId ?? msg.id
          };
        }
      }
      return {
        shouldShowWaiting: false,
        staleUnansweredMessageId: null,
        waitingMessageId: null
      };
    }, [messages, selectedUser.id, stoppedRequestIds, retryingQuestionId]);

  // Handle response timeout - show the retry card and release waiting state
  const handleResponseTimeout = useCallback(() => {
    // Keyed off the outstanding request (falling back to the awaited
    // message's own id, never a shared literal) so repeated timer fires for
    // the same still-pending request dedupe via the existing id-based dedup
    // in ChatContext instead of stacking bubbles or colliding with unrelated
    // pending requests. pendingUserMessage and waitingMessageId are updated
    // independently, so a brief desync between them can leave both null —
    // fall back to a monotonic counter rather than a shared literal so that
    // rare case can't collide with (and silently drop) an unrelated timeout.
    // A retry re-asks the same question, so its own request id comes first:
    // keyed by the question, a second timed-out retry would be deduped away.
    // `||` rather than `??`: a send that never went out has an empty request id.
    const timeoutKey =
      pendingRequestId ||
      pendingUserMessage?.requestId ||
      waitingMessageId ||
      `fallback-${timeoutFallbackCounterRef.current++}`;
    const timeoutMessage: ChatMessage = {
      id: `timeout-${timeoutKey}`,
      message: t("chat.answerFailed"),
      timestamp: Math.floor(Date.now() / 1000).toString(),
      user: dreamUser,
      isError: true
    };
    // Adding the card clears the optimistic question, so a question the
    // server never echoed is kept as a message for the card to sit under and
    // for its Retry to find.
    addMessages(
      pendingUserMessage
        ? [pendingUserMessage, timeoutMessage]
        : [timeoutMessage]
    );
    clearResponseTimeout();
  }, [
    addMessages,
    clearResponseTimeout,
    pendingRequestId,
    pendingUserMessage,
    t,
    waitingMessageId
  ]);

  // Start timeout when waiting for message, clear when response received
  useEffect(() => {
    if (waitingForMessage) {
      clearResponseTimeout();
      responseTimeoutRef.current = setTimeout(
        handleResponseTimeout,
        RESPONSE_TIMEOUT_MS
      );
    } else {
      clearResponseTimeout();
    }

    return () => clearResponseTimeout();
  }, [waitingForMessage, handleResponseTimeout, clearResponseTimeout]);

  // Memoize display messages with date separators, pending message, and waiting message
  const processedMessages = useMemo(() => {
    // Check if server echo for pending message already exists
    // This prevents showing both pending message and server echo simultaneously
    const hasPendingEcho =
      pendingUserMessage &&
      messages.some(
        (msg) =>
          msg.user?.id === pendingUserMessage.user?.id &&
          msg.message === pendingUserMessage.message
      );

    // Only add pending message if server echo hasn't arrived yet
    const baseMessages =
      pendingUserMessage && !hasPendingEcho
        ? [...messages, pendingUserMessage]
        : messages;

    // A question left unanswered gets the retry card right after it. Failed
    // answers are never stored, so this is also how a reload shows one. The
    // card is display-only — it is NOT written back to ChatContext.messages.
    const allMessages = staleUnansweredMessageId
      ? baseMessages.flatMap((msg) =>
          msg.id === staleUnansweredMessageId
            ? [
                msg,
                {
                  id: `${NO_ANSWER_MSG_PREFIX}${msg.id}`,
                  message: t("chat.answerFailed"),
                  timestamp: msg.timestamp,
                  user: dreamUser,
                  isError: true
                }
              ]
            : [msg]
        )
      : baseMessages;

    // Sort to ensure pending message is in correct position
    const sortedMessages = [...allMessages].sort(messagesCompareFn);

    let result = insertDateSeparators(sortedMessages);

    // Add or remove waiting message (but not if server echo already arrived)
    if ((shouldShowWaiting || pendingUserMessage) && !hasPendingEcho) {
      const currentWaitingMessage: ChatMessage = {
        id: ReservedMessageIds.WAITING_MSG,
        message: "Waiting for messages...",
        timestamp: getCurrentTimeUTC().toString(),
        user: dreamUser,
        thoughtMessage: thoughtMessage
      };
      result = [...result, currentWaitingMessage];
    }

    return result;
  }, [
    messages,
    pendingUserMessage,
    shouldShowWaiting,
    staleUnansweredMessageId,
    thoughtMessage,
    insertDateSeparators,
    t
  ]);

  // Update waiting state separately to avoid triggering re-renders in the main processing
  useEffect(() => {
    setWaitingForMessage(shouldShowWaiting);
  }, [shouldShowWaiting, setWaitingForMessage]);

  // Handle new messages separator based on focus timestamp
  const displayMessagesWithSeparator = useMemo(() => {
    if (lastMessageInFocusTimestamp === 0) {
      return processedMessages;
    }

    // Find the first message after the focus timestamp that's not from the user
    let separatorIndex = -1;
    for (let i = 0; i < processedMessages.length; i++) {
      const msg = processedMessages[i];
      if (
        msg.user !== undefined &&
        msg.user.id !== selectedUser.id &&
        msg.user.id !== ReservedUserIds.DREAMER_CONVERSATIONAL_ID &&
        msg.id !== ReservedMessageIds.WAITING_MSG &&
        !isDateSeparatorMessage(msg) &&
        parseInt(msg.timestamp, 10) > lastMessageInFocusTimestamp
      ) {
        separatorIndex = i;
        break;
      }
    }

    if (separatorIndex === -1) {
      return processedMessages;
    }

    const newMessagesCount = processedMessages.length - separatorIndex;
    const isSingleMessage = newMessagesCount === 1;
    const separatorMessage: ChatMessage = {
      id: ReservedMessageIds.MSG_SEPARATOR,
      message: isSingleMessage
        ? FixedMessageIds.NEW_MESSAGE
        : FixedMessageIds.NEW_MESSAGES,
      timestamp: getCurrentTimeUTC().toString(),
      user: dreamUser
    };

    return [
      ...processedMessages.slice(0, separatorIndex),
      separatorMessage,
      ...processedMessages.slice(separatorIndex)
    ];
  }, [processedMessages, lastMessageInFocusTimestamp, selectedUser.id]);

  // Update displayMessages only when the computed value changes
  useEffect(() => {
    setDisplayMessages(displayMessagesWithSeparator);
  }, [displayMessagesWithSeparator]);

  // Rows both lists render and the search hook indexes; hoisted so they share one stable reference.
  const filteredMessages = useMemo(
    () => filterMessagesBySlicer(sliceSelected, displayMessages),
    [displayMessages, sliceSelected]
  );
  // Date separators pass every tab's filter so they can render inline, but carry
  // no content of their own — a tab of separators alone is still empty.
  const filteredTabIsEmpty = useMemo(
    () => filteredMessages.every((message) => isDateSeparatorMessage(message)),
    [filteredMessages]
  );
  // Latest filteredMessages for use inside effects/timeouts without re-running them on every message change (deps stay [chatDrawerOpen, useLegacy]). Synced in a committed effect so a discarded render can't update the ref.
  const filteredMessagesRef = useRef(filteredMessages);
  useEffect(() => {
    filteredMessagesRef.current = filteredMessages;
  }, [filteredMessages]);

  // Legacy list when virtualization is off, or transiently while search is open (so DOM search walks every message, incl. lazy table cells).
  const useLegacy =
    !virtualizationEnabled || (dreamerSearchEnabled && isSearchOpen);

  // Opening search de-virtualizes; remember the top-visible message so closing search reopens Virtuoso there, not the bottom.
  useEffect(() => {
    if (isSearchOpen) setReopenAnchorId(topVisibleIdRef.current);
  }, [isSearchOpen]);

  // Clear the search anchor once Virtuoso is back and consumed it, so a later normal reopen doesn't land on a stale search position.
  useEffect(() => {
    if (chatDrawerOpen && !useLegacy && reopenAnchorId) setReopenAnchorId(null);
  }, [chatDrawerOpen, useLegacy, reopenAnchorId]);

  // While search is open, hold "scrolled up" so the snap-to-bottom observers back off (mirrors main).
  useEffect(() => {
    if (dreamerSearchEnabled && isSearchOpen) {
      userScrolledUpRef.current = true;
    }
  }, [dreamerSearchEnabled, isSearchOpen]);

  // Virtualized path: record the top-visible index+offset on close, reopen at it (initialTopMostItemIndex) and re-assert via scrollToIndex so a late chart re-measure can't drift it.
  useEffect(() => {
    // Cancel any restore timers still pending from a prior reopen before we
    // capture/schedule again — otherwise a rapid close→reopen leaves stale
    // timers that fire scrollToIndex against the previous anchor.
    virtuosoRestoreCleanupRef.current?.();
    virtuosoRestoreCleanupRef.current = null;

    const wasOpen = prevDrawerOpenForSnapshotRef.current;
    prevDrawerOpenForSnapshotRef.current = chatDrawerOpen;
    if (useLegacy) return;

    if (wasOpen && !chatDrawerOpen) {
      // Vaul keeps the portal mounted through the close animation, so DOM is still readable here.
      const scroller = virtuosoScrollerRef.current;
      if (!scroller || followBottomRef.current) {
        // Following the bottom → no anchor → reopen at the latest message.
        virtuosoRestoreRef.current = null;
        setVirtuosoRestoreAnchor(null);
        return;
      }
      const scrollerTop = scroller.getBoundingClientRect().top;
      const balloons = scroller.querySelectorAll<HTMLElement>(
        '[data-test^="chat-session-msg-index-"]'
      );
      let restore: { id: string; offset: number } | null = null;
      for (const balloon of balloons) {
        const rect = balloon.getBoundingClientRect();
        if (rect.bottom > scrollerTop + 1) {
          const dataTest = balloon.getAttribute("data-test") ?? "";
          const index = Number(
            dataTest.replace("chat-session-msg-index-", "")
          );
          const id = Number.isFinite(index)
            ? filteredMessagesRef.current[index]?.id
            : undefined;
          if (id) {
            // Px of this message scrolled above the fold (>= 0).
            restore = { id, offset: scrollerTop - rect.top };
          }
          break;
        }
      }
      virtuosoRestoreRef.current = restore;
      setVirtuosoRestoreAnchor(restore);
    } else if (!wasOpen && chatDrawerOpen) {
      // Reopened at the restore anchor via initialTopMostItemIndex; re-assert the exact offset after the portal mounts and after charts settle, then clear.
      const restore = virtuosoRestoreRef.current;
      virtuosoRestoreRef.current = null;
      setVirtuosoRestoreAnchor(null);
      if (restore) {
        const applyRestore = () => {
          const index = filteredMessagesRef.current.findIndex(
            (message) => message.id === restore.id
          );
          if (index === -1) return;
          virtuosoRef.current?.scrollToIndex({
            index,
            align: "start",
            offset: restore.offset
          });
        };
        const first = setTimeout(applyRestore, 200);
        const second = setTimeout(applyRestore, 450);
        virtuosoRestoreCleanupRef.current = () => {
          clearTimeout(first);
          clearTimeout(second);
        };
      }
    }
  }, [chatDrawerOpen, useLegacy]);

  useEffect(
    () => () => {
      virtuosoRestoreCleanupRef.current?.();
      virtuosoRestoreCleanupRef.current = null;
    },
    []
  );

  // Deep-linked draft: scroll to its card once it's in the list, on either path. Timers live in a ref, not the effect cleanup, because clearing focusDraftId re-runs this effect.
  const draftFocusCleanupRef = useRef<(() => void) | null>(null);
  // Message id Virtuoso opens at while the draft's retries run; the portal mounts after focusDraftId is already cleared.
  const [draftMountAnchorId, setDraftMountAnchorId] = useState<string | null>(
    null
  );
  // A new deep link or a closed drawer supersedes retries still pending for the previous draft.
  useEffect(() => {
    if (!focusDraftId && chatDrawerOpen) return;
    draftFocusCleanupRef.current?.();
    draftFocusCleanupRef.current = null;
    setDraftMountAnchorId(null);
  }, [focusDraftId, chatDrawerOpen]);
  useEffect(() => {
    if (!focusDraftId || !chatDrawerOpen) return;
    const findDraftIndex = () =>
      findDraftMessageIndex(filteredMessagesRef.current, focusDraftId);
    if (findDraftIndex() === -1) return;
    // Hold both paths off their open-at-bottom snap/follow so they don't pull the view back down.
    userScrolledUpRef.current = true;
    followBottomRef.current = false;
    // The draft replaces the reopen-restore on both paths; a legacy restore landing near the bottom would re-arm the snap.
    savedScrollTopRef.current = null;
    virtuosoRestoreCleanupRef.current?.();
    virtuosoRestoreCleanupRef.current = null;
    const scrollToDraft = () => {
      const index = findDraftIndex();
      if (index === -1) return;
      if (useLegacy) {
        userScrolledUpRef.current = true;
        const container = messagesContainerRef.current;
        const balloon = container?.querySelector<HTMLElement>(
          `[data-test="chat-session-msg-index-${index}"]`
        );
        if (!container || !balloon) return;
        container.scrollTop +=
          balloon.getBoundingClientRect().top -
          container.getBoundingClientRect().top;
      } else {
        followBottomRef.current = false;
        virtuosoRef.current?.scrollToIndex({ index, align: "start" });
      }
    };
    draftFocusCleanupRef.current?.();
    setDraftMountAnchorId(filteredMessagesRef.current[findDraftIndex()].id);
    const lastDelay = DRAFT_FOCUS_DELAYS_MS[DRAFT_FOCUS_DELAYS_MS.length - 1];
    const timers = DRAFT_FOCUS_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        scrollToDraft();
        if (delay === lastDelay) setDraftMountAnchorId(null);
      }, delay)
    );
    draftFocusCleanupRef.current = () => timers.forEach(clearTimeout);
    onFocusDraftHandled();
  }, [
    focusDraftId,
    chatDrawerOpen,
    filteredMessages,
    useLegacy,
    onFocusDraftHandled
  ]);

  // Give up on a deep-linked draft that never arrives (outside the loaded history, or on another tab).
  useEffect(() => {
    if (!focusDraftId) return;
    const timeout = setTimeout(onFocusDraftHandled, DRAFT_FOCUS_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [focusDraftId, onFocusDraftHandled]);

  useEffect(
    () => () => {
      draftFocusCleanupRef.current?.();
      draftFocusCleanupRef.current = null;
    },
    []
  );

  // Legacy path (mirrors main): save scrollTop on close, restore on reopen, snap to bottom on open/slice-change/new-response. State persists in ChatList. No-op while virtualized.
  useEffect(() => {
    legacyCleanupRef.current?.();
    legacyCleanupRef.current = null;

    const drawerJustOpened = chatDrawerOpen && !prevDrawerOpenRef.current;
    const drawerJustClosed = !chatDrawerOpen && prevDrawerOpenRef.current;
    prevDrawerOpenRef.current = chatDrawerOpen;

    if (!useLegacy) return;

    const lastRealMessage = [...filteredMessages]
      .reverse()
      .find((msg) => !isReservedMessage(msg) && !isDateSeparatorMessage(msg));
    const currentLastMessageId = lastRealMessage?.id || "";
    const hasNewMessages =
      currentLastMessageId !== "" &&
      currentLastMessageId !== prevLastMessageIdRef.current &&
      prevLastMessageIdRef.current !== "";

    const sliceChanged =
      sliceSelected !== prevSliceRef.current && prevSliceRef.current !== "";
    const receivedResponse =
      lastResponseRequestId !== null &&
      lastResponseRequestId !== prevResponseRequestIdRef.current;

    // Save scroll position when the drawer closes so we can restore it on reopen.
    if (drawerJustClosed) {
      const container = messagesContainerRef.current;
      if (container) savedScrollTopRef.current = container.scrollTop;
    }

    // On reopen, restore the saved position (only if scrolled up; else fall through to the snap below).
    if (drawerJustOpened && userScrolledUpRef.current) {
      const restoreScroll = () => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const saved = savedScrollTopRef.current;
        if (saved === null) return;
        isScrollingToBottomRef.current = true;
        // `behavior: "instant"` cancels any in-flight smooth-scroll animation.
        container.scrollTo({ top: saved, behavior: "instant" });
        isScrollingToBottomRef.current = false;
        const distFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distFromBottom <= 100) userScrolledUpRef.current = false;
      };
      // Fire at 50ms (beat browser scroll restoration) and 300ms (beat late portal mount / smooth-scroll).
      const first = setTimeout(restoreScroll, 50);
      const second = setTimeout(restoreScroll, 300);
      legacyCleanupRef.current = () => {
        clearTimeout(first);
        clearTimeout(second);
      };
    }

    // Snap to bottom on open (when not restoring), slice change, or new response.
    const shouldScroll =
      chatDrawerOpen &&
      ((!userScrolledUpRef.current && drawerJustOpened) ||
        sliceChanged ||
        receivedResponse ||
        hasNewMessages);

    if (shouldScroll && !userScrolledUpRef.current) {
      const snapToBottom = () => {
        const container = messagesContainerRef.current;
        if (!container || userScrolledUpRef.current) return;
        isScrollingToBottomRef.current = true;
        container.scrollTop = container.scrollHeight;
        isScrollingToBottomRef.current = false;
      };
      // Vaul's portal mounts ~180ms late: rAF-poll for the container, then snap + attach a MutationObserver for late content.
      let cancelled = false;
      let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
      let attempts = 0;
      const trySnap = () => {
        if (cancelled || userScrolledUpRef.current) return;
        const container = messagesContainerRef.current;
        if (container) {
          snapToBottom();
          let lastScrollHeight = container.scrollHeight;
          const mo = new MutationObserver(() => {
            const container = messagesContainerRef.current;
            if (
              !container ||
              container.scrollHeight === lastScrollHeight ||
              userScrolledUpRef.current
            )
              return;
            lastScrollHeight = container.scrollHeight;
            snapToBottom();
          });
          mo.observe(container, { childList: true, subtree: true });
          const moTimeoutId = setTimeout(() => mo.disconnect(), 5000);
          legacyCleanupRef.current = () => {
            mo.disconnect();
            clearTimeout(moTimeoutId);
          };
        } else if (attempts++ < 30) {
          rafId = requestAnimationFrame(trySnap);
        }
      };
      rafId = requestAnimationFrame(trySnap);
      legacyCleanupRef.current = () => {
        cancelled = true;
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }

    prevLastMessageIdRef.current = currentLastMessageId;
    prevSliceRef.current = sliceSelected;
    prevResponseRequestIdRef.current = lastResponseRequestId;

    return () => {
      legacyCleanupRef.current?.();
      legacyCleanupRef.current = null;
    };
  }, [
    filteredMessages,
    sliceSelected,
    chatDrawerOpen,
    lastResponseRequestId,
    useLegacy
  ]);

  // Legacy path: snap to bottom on bubble height growth (chart/data load) if near it; rAF-coalesced. No-op while virtualized.
  useEffect(() => {
    if (!chatDrawerOpen || !useLegacy) return;

    let pollCancelled = false;
    let pollRafId: ReturnType<typeof requestAnimationFrame> | null = null;
    let snapRafId: number | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let pollAttempts = 0;

    const attach = () => {
      if (pollCancelled) return;
      const container = messagesContainerRef.current;
      if (!container) {
        if (pollAttempts++ < 30) {
          pollRafId = requestAnimationFrame(attach);
        }
        return;
      }
      const messageNodes = container.querySelectorAll<HTMLElement>(
        '[data-test^="chat-session-msg-index-"]'
      );
      if (messageNodes.length === 0) return;

      const snap = () => {
        if (snapRafId !== null) cancelAnimationFrame(snapRafId);
        snapRafId = requestAnimationFrame(() => {
          snapRafId = null;
          if (userScrolledUpRef.current) return;
          const distFromBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight;
          if (distFromBottom > 0) {
            isScrollingToBottomRef.current = true;
            container.scrollTop = container.scrollHeight;
            isScrollingToBottomRef.current = false;
          }
        });
      };
      resizeObserver = new ResizeObserver(snap);
      messageNodes.forEach((node) => resizeObserver!.observe(node));
    };

    pollRafId = requestAnimationFrame(attach);

    return () => {
      pollCancelled = true;
      if (pollRafId !== null) cancelAnimationFrame(pollRafId);
      resizeObserver?.disconnect();
      if (snapRafId !== null) cancelAnimationFrame(snapRafId);
    };
  }, [filteredMessages, chatDrawerOpen, useLegacy]);

  // Ids already animated — so scrolling a message out of overscan and back never replays it.
  const animatedIdsRef = useRef<Set<string>>(new Set());
  // Freshly-appended ids via the "info from previous renders" pattern (no ref writes in render, so a replayed render can't desync the gate). See computeFreshMessageIds.
  const [prevFilteredForAnim, setPrevFilteredForAnim] =
    useState<ChatMessage[]>(filteredMessages);
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(
    () => new Set()
  );
  if (prevFilteredForAnim !== filteredMessages) {
    setPrevFilteredForAnim(filteredMessages);
    setNewMessageIds(
      computeFreshMessageIds(
        prevFilteredForAnim,
        filteredMessages,
        animatedIdsRef.current
      )
    );
  }

  // Mark flagged ids animated after their mount render, so a later remount doesn't replay.
  useEffect(() => {
    newMessageIds.forEach((id) => animatedIdsRef.current.add(id));
  }, [newMessageIds]);

  // Submitting re-engages follow and jumps to the newest message (the pin/snap keeps it there as the reply grows).
  useEffect(() => {
    if (!pendingUserMessage) return;
    if (useLegacy) {
      userScrolledUpRef.current = false;
    } else {
      followBottomRef.current = true;
      pinVirtualizedToBottom(true);
    }
  }, [pendingUserMessage, useLegacy, pinVirtualizedToBottom]);

  const renderVirtualBalloon = useCallback(
    (index: number, message: ChatMessage) => {
      // User messages appear instantly; Dreamer replies animate only when genuinely new.
      const isUserMessage = message.user?.id === selectedUser.id;
      return (
        <ChatBalloon
          message={message}
          selectedUser={selectedUser}
          index={index}
          skipAnimation={isUserMessage}
          animateEntry={
            newMessageIds.has(message.id) &&
            !animatedIdsRef.current.has(message.id)
          }
          enableLayoutExit={false}
          onRetry={onRetry}
          onRetryFailedAnswer={onRetryFailedAnswer}
          onRegenerateDraft={onRegenerateDraft}
          onSelectDraftVersion={onSelectDraftVersion}
          isRegeneratingDraft={regeneratingDraftMessageIds.has(message.id)}
          recipientFirstName={recipientFirstName}
          onDownloadAttachment={downloadAttachment}
        />
      );
    },
    [
      selectedUser,
      onRetry,
      onRetryFailedAnswer,
      onRegenerateDraft,
      onSelectDraftVersion,
      regeneratingDraftMessageIds,
      newMessageIds,
      recipientFirstName,
      downloadAttachment
    ]
  );

  // Where Virtuoso opens on mount: search anchor if set, else the latest message.
  const initialTopMostItemIndex = useMemo(() => {
    if (reopenAnchorId) {
      const anchorIndex = filteredMessages.findIndex(
        (message) => message.id === reopenAnchorId
      );
      if (anchorIndex !== -1) {
        return { index: anchorIndex, align: "start" as const };
      }
    }
    return { index: filteredMessages.length - 1, align: "end" as const };
  }, [filteredMessages, reopenAnchorId]);

  // Drawer-reopen anchor takes precedence: resolve the captured message id to its
  // current index (by id, so an insert above it while closed can't shift it), so
  // Virtuoso opens there immediately instead of flashing the latest first.
  const restoreAnchorIndex = virtuosoRestoreAnchor
    ? filteredMessages.findIndex(
        (message) => message.id === virtuosoRestoreAnchor.id
      )
    : -1;
  // A deep-linked draft outranks both: that card is where the user asked to land.
  const draftAnchorIndex = draftMountAnchorId
    ? filteredMessages.findIndex((message) => message.id === draftMountAnchorId)
    : -1;
  const virtuosoInitialTopMostItemIndex =
    draftAnchorIndex !== -1
      ? { index: draftAnchorIndex, align: "start" as const }
      : restoreAnchorIndex === -1
        ? initialTopMostItemIndex
        : { index: restoreAnchorIndex, align: "start" as const };

  const drawerContent = (
    <AppDrawerContent
      variant="chat"
      initialWidth={480}
      onResizeEnd={handleResizeEnd}
      renderContent={({ isHoveringEdge, isResizing }) => (
        <div
          className={cn(
            "w-full overflow-hidden h-full flex flex-col grow relative",
            "bg-surface-elevation1 rounded-2xl border border-border-border1 shadow-[0_0_24px_4px_rgba(0,0,0,0.35)] backdrop-blur-[35px]"
          )}
        >
          <div
            className={cn(
              "absolute left-0 top-0 bottom-0 w-[2px] rounded-l-2xl pointer-events-none z-10",
              "transition-opacity duration-150",
              isHoveringEdge || isResizing ? "opacity-100" : "opacity-0"
            )}
            style={{
              background:
                "linear-gradient(to bottom, transparent, rgba(246, 248, 251, 0.40) 30%, rgba(246, 248, 251, 0.40) 70%, transparent)"
            }}
          />
          <DrawerHeader divider={false} className="block h-auto p-0">
            <div className="flex flex-col items-stretch">
              <div className="flex justify-between items-center px-4 pt-4 pb-2">
                <div className="flex items-center gap-2">
                  <IconContainer size="md">
                    <DreamhubGradient />
                  </IconContainer>
                  <Text as="div" variant="lgSubtitleH1">
                    {t("dreamer")}
                  </Text>
                  <span className="ml-1">
                    <BetaBadge />
                  </span>
                </div>
                <div className="flex items-center gap-2 z-10">
                  <FeedbackButton
                  tooltip={t("feedback")} idContext="chat" size="md" />
                  {dreamerSearchEnabled && !reportsTabSelected && (
                    <IconButton
                      ref={searchToggleRef}
                      variant="iconNoBg"
                      size="md"
                      onClick={toggleSearch}
                      aria-label={
                        isSearchOpen
                          ? t("chat.search.close")
                          : t("chat.search.open")
                      }
                      data-test="dreamer-search-open"
                    >
                      <Search />
                    </IconButton>
                  )}
                  <IconButton
                    variant="iconNoBg"
                    size="md"
                    onClick={() => setChatDrawerOpen(false)}
                  >
                    <Close />
                  </IconButton>
                </div>
              </div>
              <LayoutTabs
                className="text-base"
                options={Object.fromEntries(
                  tabIds.map((id) => [
                    id,
                    id === "reports" && unseenReportsCount > 0 ? (
                      <span className="flex items-center gap-[6px]">
                        {t(`chat.tabs.${id}`)}
                        <CounterBadge
                          count={unseenReportsCount}
                          variant="notification"
                        />
                      </span>
                    ) : (
                      t(`chat.tabs.${id}`)
                    )
                  ])
                )}
                icons={TAB_ICONS}
                defaultValue={sliceSelected}
                valueSelected={handleSlicer}
              />
            </div>
          </DrawerHeader>
          {dreamerSearchEnabled && isSearchOpen && !reportsTabSelected && (
            <DreamerSearchBar search={search} onClose={closeSearch} />
          )}
          {reportsTabEnabled && (
            <ReportPreviewHost
              report={focusedReport}
              onClose={clearFocusedReport}
            />
          )}
          <div
            className="relative flex-1 min-h-0 w-full overflow-x-hidden flex flex-col"
            data-test="chat-container-wrapper"
          >
            {reportsTabSelected ? (
              <ReportsPanel />
            ) : filteredTabIsEmpty ? (
              <Text
                as="div"
                variant="bodySecondary"
                className="h-full w-full text-center self-center content-center"
              >
                {t("chat.empty", {
                  type: t(`chat.tabMessageTypes.${sliceSelected}`)
                })}
              </Text>
            ) : useLegacy ? (
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                onWheel={handleUserWheel}
                onTouchStart={handleUserTouchStart}
                onTouchMove={handleUserTouchMove}
                className="w-full overflow-y-auto overflow-x-hidden h-full flex flex-col relative"
                data-test="chat-container"
                data-id="chat-container"
              >
                <AnimatePresence>
                  {filteredMessages.map((message, index) => {
                    // User messages appear instantly; AI responses animate. Legacy keeps framer layout + exit.
                    const isUserMessage =
                      message.user?.id === selectedUser.id;
                    return (
                      <ChatBalloon
                        key={`chat-balloon-${message.id}`}
                        message={message}
                        selectedUser={selectedUser}
                        index={index}
                        skipAnimation={isUserMessage}
                        animateEntry
                        enableLayoutExit
                        onRetry={onRetry}
                        onRetryFailedAnswer={onRetryFailedAnswer}
                        onRegenerateDraft={onRegenerateDraft}
                        onSelectDraftVersion={onSelectDraftVersion}
                        isRegeneratingDraft={regeneratingDraftMessageIds.has(
                          message.id
                        )}
                        recipientFirstName={recipientFirstName}
                        onDownloadAttachment={downloadAttachment}
                      />
                    );
                  })}
                </AnimatePresence>
                <div
                  className={cn(
                    "sticky bottom-3 flex justify-center pointer-events-none z-20 transition-opacity duration-200 -mt-9",
                    showLegacyScrollToBottom ? "opacity-100" : "opacity-0"
                  )}
                >
                  <button
                    type="button"
                    aria-label={t("chat.scrollToBottom")}
                    onClick={scrollToBottomLegacy}
                    className={cn(
                      "relative flex items-center justify-center size-9 rounded-full border-[0.5px] border-border-border2 bg-surface-elevation2 before:absolute before:inset-0 before:rounded-full before:transition-colors before:duration-150 hover:before:bg-surface-hover-soft active:before:bg-surface-focus-selected",
                      showLegacyScrollToBottom
                        ? "pointer-events-auto"
                        : "pointer-events-none"
                    )}
                    style={{
                      boxShadow: "0px 8px 16px rgba(12, 17, 29, 0.8)"
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5V19M12 19L19 12M12 19L5 12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-primary"
                      />
                    </svg>
                  </button>
                </div>
                <div
                  className="h-8 w-full shrink-0 sticky bottom-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(22, 30, 44, 0.00) 0%, #161E2C 75%, #161E2C 100%)"
                  }}
                />
              </div>
            ) : (
              <>
                <Virtuoso
                  ref={virtuosoRef}
                  scrollerRef={setVirtuosoScroller}
                  data={filteredMessages}
                  className="h-full w-full"
                  components={VIRTUOSO_COMPONENTS}
                  computeItemKey={(_, message) =>
                    `chat-balloon-${message.id}`
                  }
                  itemContent={renderVirtualBalloon}
                  // Open at the drawer-reopen anchor, the search anchor, or the latest message.
                  initialTopMostItemIndex={virtuosoInitialTopMostItemIndex}
                  // Remember the topmost rendered message so search can restore it.
                  rangeChanged={(range) => {
                    topVisibleIdRef.current =
                      filteredMessages[range.startIndex]?.id ?? null;
                  }}
                  // Re-pin on any height delta while following — covers a new message and the last item growing/shrinking after (table/chart loading rows).
                  totalListHeightChanged={() => {
                    if (followBottomRef.current) pinVirtualizedToBottom();
                  }}
                  // Toggles the scroll-to-bottom button; returning to bottom re-engages follow.
                  atBottomStateChange={(atBottom) => {
                    setShowScrollToBottom(!atBottom);
                    if (atBottom) followBottomRef.current = true;
                  }}
                  atBottomThreshold={VIRTUOSO_AT_BOTTOM_THRESHOLD}
                  increaseViewportBy={VIRTUOSO_VIEWPORT_MARGIN}
                  defaultItemHeight={120}
                  // Applies size corrections in-frame — the documented dynamic-height flicker fix.
                  skipAnimationFrameInResizeObserver
                />
                <div
                  className={cn(
                    "absolute inset-x-0 bottom-3 flex justify-center pointer-events-none z-20 transition-opacity duration-200",
                    showScrollToBottom ? "opacity-100" : "opacity-0"
                  )}
                >
                  <button
                    type="button"
                    aria-label={t("chat.scrollToBottom")}
                    onClick={() => {
                      followBottomRef.current = true;
                      pinVirtualizedToBottom(true);
                    }}
                    className={cn(
                      "relative flex items-center justify-center size-9 rounded-full border-[0.5px] border-border-border2 bg-surface-elevation2 before:absolute before:inset-0 before:rounded-full before:transition-colors before:duration-150 hover:before:bg-surface-hover-soft active:before:bg-surface-focus-selected",
                      showScrollToBottom
                        ? "pointer-events-auto"
                        : "pointer-events-none"
                    )}
                    style={{
                      boxShadow: "0px 8px 16px rgba(12, 17, 29, 0.8)"
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5V19M12 19L19 12M12 19L5 12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-primary"
                      />
                    </svg>
                  </button>
                </div>
                <div
                  className="h-8 w-full absolute inset-x-0 bottom-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(22, 30, 44, 0.00) 0%, #161E2C 75%, #161E2C 100%)"
                  }}
                />
              </>
            )}
          </div>
          {reportsTabSelected ||
          sliceSelected === "alerts" ||
          sliceSelected === "starred" ? null : (
            <DrawerFooter
              onFocus={() => handleFocus()}
              className="gap-1 px-5 pt-0 pb-2"
            >
              {/* Temporary removed until it's functional */}
              {/* <div className="flex flex-row items-start gap-2">
              <Button data-test="dreamer-suggestions" variant="secondary">
                {t("suggestions")}
              </Button>
              <Button data-test="dreamer-actions" variant="secondary">
                {t("actions")}
              </Button>
            </div> */}
              <NewChatBottomBar
                selectedUser={selectedUser}
                sendMessage={sendMessageCallback}
                lastMessageFromSelectedUser={waitingForMessage}
                onStopResponse={onStopResponse}
              />
              <Text variant="smBodyTertiary" className="text-center">
                {t("chat.disclaimer")}
              </Text>
            </DrawerFooter>
          )}
        </div>
      )}
    />
  );

  return (
    <ChatTabsProvider value={chatTabs}>{drawerContent}</ChatTabsProvider>
  );
}
